import { useEffect, useRef, useState } from 'react'

/**
 * Custom useInView hook that returns true/false only if component is mounted and element is present,
 * otherwise returns -1.
 *
 * @param ref - React ref object pointing to the element to observe
 * @param options - Optional IntersectionObserver options (root, rootMargin, threshold)
 * @returns true if element is in view, false if not in view, -1 if not mounted or element doesn't exist
 */
export const useInView = <T extends HTMLElement = HTMLElement>(
  ref: React.RefObject<T>,
  options?: IntersectionObserverInit
): boolean | -1 => {
  const [isInView, setIsInView] = useState<boolean | -1>(-1)
  const isMounted = useRef(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      setIsInView(-1)
    }
  }, [])

  useEffect(() => {
    // Check if component is mounted and element exists
    if (!isMounted.current) {
      setIsInView(-1)
      return
    }

    const element = ref.current

    if (!element) {
      setIsInView(-1)
      return
    }

    // Create IntersectionObserver
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        // Only update if component is still mounted and element still exists
        if (isMounted.current && ref.current) {
          setIsInView(entry.isIntersecting)
        } else {
          setIsInView(-1)
        }
      },
      {
        threshold: 0,
        ...options
      }
    )

    observerRef.current.observe(element)

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }, [ref, options])

  // Return the state value directly - effects will manage setting it to -1 when needed
  return isInView
}
